/* eslint-disable no-else-return */
import BigNumber from 'bignumber.js';

import { MarketsStoreView } from '#/features/perpetuals/market-info/markets-context';
import { MarketsSummaryStoreView } from '#/features/perpetuals/market-info/markets-summary-context';
import { OpenPositionsStoreView } from '#/features/perpetuals/open-positions-context';
import { OpenOrdersStoreView } from '#/features/perpetuals/trade-overview/open-orders-context';
import { calcOpenNotional } from '#/features/perpetuals/trade-overview/position-functions';

import calculate_fee_provision from './calculate_fee_provision';
import { calculate_open_loss } from './calculate_open_loss';

/**
 * Adapted from
 * @see Paradex Pepe Calculator
 */
export default function calculate_margin_requirement(
  _margin_check: 'Initial',
  asset: string, // e.g. 'SOL-USD-PERP'
  openPositionsStore: OpenPositionsStoreView,
  marketsSummaryStore: MarketsSummaryStoreView,
  marketsStore: MarketsStoreView,
  openOrdersStore: OpenOrdersStoreView,
): BigNumber {
  const assetOpenPosition = openPositionsStore.get(asset);
  const marketSummary = marketsSummaryStore.getMarketSummary(asset);
  const market = marketsStore.getMarket(asset);
  const assetOpenOrders = openOrdersStore
    .getAll()
    .filter((order) => order.market === asset);

  if (marketSummary == null) {
    throw new Error(
      `calculate_margin_requirement: asset='${asset}' Market Summary is not available`,
    );
  }
  if (market == null) {
    throw new Error(
      `calculate_margin_requirement: asset='${asset}' Market is not available`,
    );
  }

  const openPositions = assetOpenPosition == null ? [] : [assetOpenPosition];
  const openNotional = calcOpenNotional(
    assetOpenOrders,
    openPositions,
    marketsStore.getMarket,
    marketsSummaryStore.getMarketSummary,
  );
  const openLoss = calculate_open_loss(
    asset,
    openOrdersStore,
    marketsSummaryStore,
  );
  const feeProvision = calculate_fee_provision(
    asset,
    openPositionsStore,
    marketsSummaryStore,
    openOrdersStore,
  );

  const { imf_base } = market.delta1_cross_margin_params;
  const { imf_factor } = market.delta1_cross_margin_params;
  const { imf_shift } = market.delta1_cross_margin_params;
  /** max( imf_base , imf_factor * SQRT( MAX( 0 , openNotional - imf_shift ) ) ) */
  const IMF = BigNumber.maximum(
    imf_base,
    imf_factor.multipliedBy(
      BigNumber.maximum(0, openNotional.minus(imf_shift)).sqrt(),
    ),
  );

  const initial_margin_requirement = openNotional
    .multipliedBy(IMF)
    .plus(openLoss)
    .plus(feeProvision);

  return initial_margin_requirement;
}
